import type { DragEndEvent } from "@dnd-kit/core";
import { closestCenter, DndContext, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { DragHandle } from "@/components/DragHandle";
import { Icon } from "@/components/Icon";
import { Skeleton } from "@/components/Skeleton";
import type { Column, SortConfig, TableDensity } from "./DataTable.types";

const densityClasses = {
  compact: "py-1.5 px-3 text-sm",
  default: "py-3 px-4 text-sm",
  relaxed: "py-4 px-4 text-base",
};

const alignmentClasses = {
  left: "text-left justify-start",
  center: "text-center justify-center",
  right: "text-right justify-end",
};

function getColumnStyles<T>(column: Column<T>) {
  const styles: React.CSSProperties = {};

  if (column.width !== undefined) {
    styles.width = typeof column.width === "number" ? `${column.width}px` : column.width;
  }
  if (column.minWidth !== undefined) {
    styles.minWidth = typeof column.minWidth === "number" ? `${column.minWidth}px` : column.minWidth;
  }
  if (column.maxWidth !== undefined) {
    styles.maxWidth = typeof column.maxWidth === "number" ? `${column.maxWidth}px` : column.maxWidth;
  }
  if (column.flex !== undefined) {
    styles.flex = `${column.flex} 1 0`;
  }

  return styles;
}

interface SortableHeaderCellProps<T> {
  column: Column<T>;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  isFirstColumn?: boolean;
  density?: TableDensity;
  isLoading?: boolean;
  /** Replaces the label for this header — see `TableHeaderCell.headerContent`. */
  headerContent?: ReactNode;
  /** See `DataTableProps.sortNote`. Applies only when this column is the sorted one. */
  sortNote?: string;
}

function SortableHeaderCell<T>({
  column,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  isFirstColumn,
  density = "default",
  isLoading = false,
  headerContent,
  sortNote,
}: SortableHeaderCellProps<T>) {
  const isReorderable = column.reorderable !== false;
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: column.id,
    disabled: !isReorderable || isLoading,
  });

  const isSorted = sortConfig?.columnId === column.id;
  const sortDirection = isSorted ? sortConfig.direction : null;
  const isSticky = stickyFirstColumn && isFirstColumn;
  // The note is about the sort, so it only exists where the sort is.
  const note = isSorted ? sortNote : undefined;

  const style: React.CSSProperties = {
    ...getColumnStyles(column),
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
    zIndex: isDragging ? 50 : undefined,
  };

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        "flex items-center border-b border-border-default bg-background-surface",
        column.sortable ? "gap-2" : "gap-1",
        densityClasses[density],
        alignmentClasses[column.align || "left"],
        stickyHeader && "sticky top-0 z-10",
        isSticky && "sticky left-0 z-20 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.1)]",
        stickyHeader && isSticky && "z-30",
        column.sortable && !isLoading && "hover:bg-background-hover",
        isLoading && "cursor-not-allowed opacity-60",
        isSorted ? "font-bold text-text-primary" : "font-medium text-text-secondary",
        isDragging && "cursor-grabbing bg-background-hover shadow-md ring-2 ring-inset ring-action-brand"
      )}
      style={style}
      data-dragging={isDragging || undefined}
      role="columnheader"
      // Untouched by `note`: the column really is sorted, and saying otherwise
      // here would hide the sort from assistive tech instead of explaining it.
      aria-sort={isSorted ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
      // dnd-kit derives its own `aria-disabled` from `disabled`, which is also true for
      // a merely non-reorderable column — a header that cannot be dragged is not a
      // disabled header. Loading is the only thing that disables this one.
      aria-disabled={isLoading}
      title={note}
      data-sort-note={note}
    >
      {isLoading ? (
        <Skeleton className="h-4 w-24" />
      ) : (
        <>
          {isReorderable && (
            <DragHandle
              ref={setActivatorNodeRef}
              {...attributes}
              {...listeners}
              aria-label={`Reorder ${column.label} column`}
              isDragging={isDragging}
            />
          )}
          {headerContent ?? (
            <span
              // The pointer is a promise about what a click does. On a column that cannot
              // be sorted the guarded handler below does nothing, so the label is text.
              className={twMerge("flex-1 truncate", column.sortable && "cursor-pointer")}
              onClick={() => !isLoading && column.sortable && onSort(column.id)}
            >
              {column.label}
            </span>
          )}
          {note && <span className="sr-only">{note}</span>}
          {/*
           * Two different things wear the same caret. On a sortable column it is a
           * control, and it is a button. On a column that is merely *sorted* — a table
           * ordered by something the viewer cannot re-order, e.g. a board whose sort
           * lives in its saved configuration — it is a read-out, and a button there
           * would be a control that answers nothing.
           */}
          {column.sortable ? (
            <button
              type="button"
              className="flex flex-shrink-0 cursor-pointer"
              onClick={() => !isLoading && onSort(column.id)}
              aria-label={`Sort by ${column.label}`}
            >
              {isSorted ? (
                <Icon
                  name={sortDirection === "asc" ? "CaretUp" : "CaretDown"}
                  size={14}
                  weight={note ? "regular" : "bold"}
                  className={note ? "text-text-secondary opacity-40" : "text-text-primary"}
                />
              ) : (
                <Icon name="CaretUpDown" size={12} className="text-text-secondary opacity-40" />
              )}
            </button>
          ) : isSorted ? (
            <span className="flex flex-shrink-0">
              <Icon
                name={sortDirection === "asc" ? "CaretUp" : "CaretDown"}
                size={14}
                weight={note ? "regular" : "bold"}
                className={note ? "text-text-secondary opacity-40" : "text-text-primary"}
              />
            </span>
          ) : null}
        </>
      )}
    </div>
  );
}

interface SortableHeaderRowProps<T> {
  columns: Column<T>[];
  columnOrder: string[];
  onColumnOrderChange: (newOrder: string[]) => void;
  sortConfig: SortConfig | null;
  onSort: (columnId: string) => void;
  stickyHeader?: boolean;
  stickyFirstColumn?: boolean;
  density?: TableDensity;
  isLoading?: boolean;
  /**
   * Per-column header override, so a reorderable table's headers agree with a
   * plain one's. Returns `undefined` for a column that should render its label.
   */
  renderHeaderContent?: (column: Column<T>) => ReactNode;
  /** See `DataTableProps.sortNote`. */
  sortNote?: string;
}

export function SortableHeaderRow<T>({
  columns,
  columnOrder,
  onColumnOrderChange,
  sortConfig,
  onSort,
  stickyHeader,
  stickyFirstColumn,
  density = "default",
  isLoading = false,
  renderHeaderContent,
  sortNote,
}: SortableHeaderRowProps<T>) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Create a map for quick column lookup
  const columnMap = new Map(columns.map((col) => [col.id, col]));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = columnOrder.indexOf(active.id as string);
      const newIndex = columnOrder.indexOf(over.id as string);

      // Check if the drop would displace a non-reorderable column
      const targetColumn = columnMap.get(over.id as string);
      if (targetColumn?.reorderable === false) {
        return;
      }

      // Prevent dropping before any non-reorderable columns
      // Find the first reorderable index (after all leading non-reorderable columns)
      let firstReorderableIndex = 0;
      for (let i = 0; i < columnOrder.length; i++) {
        const col = columnMap.get(columnOrder[i]);
        if (col?.reorderable === false) {
          firstReorderableIndex = i + 1;
        } else {
          break;
        }
      }

      if (newIndex < firstReorderableIndex) {
        return;
      }

      // Check if the move would displace any non-reorderable column
      // by checking if any locked column exists between old and new positions
      const minIndex = Math.min(oldIndex, newIndex);
      const maxIndex = Math.max(oldIndex, newIndex);
      for (let i = minIndex; i <= maxIndex; i++) {
        if (i === oldIndex) continue; // Skip the column being dragged
        const col = columnMap.get(columnOrder[i]);
        if (col?.reorderable === false) {
          return; // Abort if moving would displace a locked column
        }
      }

      const newOrder = arrayMove(columnOrder, oldIndex, newIndex);
      onColumnOrderChange(newOrder);
    }
  };

  // Get ordered columns
  const orderedColumns = columnOrder
    .map((id) => columnMap.get(id))
    .filter((col): col is Column<T> => col !== undefined);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
        <div className="contents" role="rowgroup">
          {orderedColumns.map((column, index) => (
            <SortableHeaderCell
              key={column.id}
              column={column}
              sortConfig={sortConfig}
              onSort={onSort}
              stickyHeader={stickyHeader}
              stickyFirstColumn={stickyFirstColumn}
              isFirstColumn={index === 0}
              density={density}
              isLoading={isLoading}
              headerContent={renderHeaderContent?.(column)}
              sortNote={sortNote}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
